import React, { useState, useEffect, useRef, useCallback, startTransition, useDeferredValue } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  Grid,
  Card,
  CardActions,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import EditIcon from '@mui/icons-material/Edit';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import { SavingOverlay } from '../../core/public/SavingOverlay';
import MediaLightbox from './MediaLightbox';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { uploadMedia, getMedias, deleteMedia, getMediaLinks, linkMedia, unlinkMedia, updateMediaOrder, updateMediaMetadata } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { isImageMimeOrExtension, isVideoMimeOrExtension } from './mediaMime';
import { getFileIcon, formatFileSize } from './mediaDisplay';
import { resolveDesktopOpenContext } from './mediaScopeUtils';
import {
  extractFilesFromDataTransfer,
  filesMatchAccept,
  extractFilesFromClipboardApi,
} from './clipboardFiles';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
export const PRODUCT_DEFAULT_IMAGE = '/images/product-default.svg';

let lastActiveGalleryKey = null;
const pasteGalleryRegistry = [];

function getGalleryKey(metadataKey, metadataValue) {
  return `${metadataKey}:${metadataValue}`;
}

function metadataValueMatches(metaValue, scopeValue) {
  if (metaValue == null || scopeValue == null) return false;
  const isObject = typeof metaValue === 'object';
  if (isObject) {
    return String(metaValue.id) === String(scopeValue);
  }
  return String(metaValue) === String(scopeValue);
}

function mediaMatchesMetadataScope(media, metadataKey, metadataValue) {
  if (!media?.metadata?.length) return false;
  return media.metadata.some(
    (meta) => meta.key === metadataKey && metadataValueMatches(meta.value, metadataValue),
  );
}

function getAcceptHintKey(accept) {
  const normalized = String(accept || '').trim().toLowerCase();
  if (normalized === 'image/*' || normalized === 'image') {
    return 'media.drop_zone_hint_images';
  }
  if (normalized === '*/*' || normalized === '*') {
    return 'media.drop_zone_hint_any';
  }
  return 'media.drop_zone_hint_generic';
}

const ORDER_MEDIA_METADATA_KEYS = new Set([
  'order_status_draft',
  'order_attachment',
  'order_gallery',
  'order_status_history',
]);

function isOrderMediaMetadataKey(metadataKey) {
  return ORDER_MEDIA_METADATA_KEYS.has(metadataKey);
}

const mediaThumbAreaSx = (compact = false) => ({
  position: 'relative',
  width: '100%',
  aspectRatio: '1 / 1',
  maxHeight: compact ? 72 : { xs: 96, sm: 104, md: 112 },
  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  overflow: 'hidden',
  flexShrink: 0,
  borderBottom: '1px solid',
  borderColor: 'divider',
});

const mediaThumbImgSx = {
  maxWidth: '100%',
  maxHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
  display: 'block',
};

function SortableMediaItem({ media, onPreview, onDelete, onEditTitle, readOnly, compact = false, showFolderPathHint = false }) {
  const { translate } = useTranslation();
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(media.metadataJSON?.title || media.filename);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: media.id });

  useEffect(() => {
    setTitleValue(media.metadataJSON?.title || media.filename);
  }, [media.metadataJSON?.title, media.filename]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleTitleSave = () => {
    if (onEditTitle && media.metadataJSON) {
      onEditTitle(media.id, titleValue);
    }
    setEditingTitle(false);
  };

  const handleTitleCancel = () => {
    setTitleValue(media.metadataJSON?.title || media.filename);
    setEditingTitle(false);
  };

  const isImage = isImageMimeOrExtension(media.mime_type, media.filename);
  const isVideo = !isImage && isVideoMimeOrExtension(media.mime_type, media.filename);
  const fileIcon = getFileIcon(media.mime_type, media.filename, 36);

  return (
    <Grid item xs={compact ? 4 : 6} sm={compact ? 3 : 3} md={compact ? 2 : 2} lg={compact ? 2 : 2} ref={setNodeRef} style={style}>
      <Card
        sx={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          bgcolor: 'background.paper',
        }}
      >
        {!readOnly && (
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              left: 6,
              zIndex: 2,
              cursor: 'grab',
              '&:active': {
                cursor: 'grabbing',
              },
            }}
            {...attributes}
            {...listeners}
          >
            <IconButton
              size="small"
              sx={(theme) => ({
                width: 28,
                height: 28,
                p: 0.25,
                bgcolor: alpha(theme.palette.background.paper, 0.95),
                boxShadow: 1,
              })}
            >
              <DragIndicatorIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}
        <Box
          onClick={() => onPreview(media)}
          sx={{
            ...mediaThumbAreaSx(compact),
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
            },
          }}
        >
          {isImage ? (
            <Box
              component="img"
              src={`${API_URL}${media.file_path}`}
              alt={media.filename}
              sx={mediaThumbImgSx}
              loading="lazy"
            />
          ) : isVideo ? (
            <Box
              component="video"
              src={`${API_URL}${media.file_path}`}
              muted
              playsInline
              preload="metadata"
              sx={{ ...mediaThumbImgSx, pointerEvents: 'none' }}
            />
          ) : (
            fileIcon
          )}
        </Box>
        <Box
          sx={{
            px: 0.75,
            pt: 0.75,
            pb: 0.25,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {editingTitle && !readOnly ? (
            <Box>
              <TextField
                fullWidth
                size="small"
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleTitleSave();
                  } else if (e.key === 'Escape') {
                    handleTitleCancel();
                  }
                }}
                autoFocus
                sx={{ '& .MuiInputBase-root': { fontSize: '0.75rem' } }}
              />
              {showFolderPathHint && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem', lineHeight: 1.2 }}>
                  {translate('media.folder_path_hint')}
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <Typography
                variant="caption"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textAlign: 'center',
                  cursor: readOnly ? 'default' : 'pointer',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  lineHeight: 1.25,
                  mb: 0.25,
                  wordBreak: 'break-word',
                  '&:hover': readOnly ? {} : { textDecoration: 'underline' },
                }}
                onClick={() => !readOnly && setEditingTitle(true)}
                title={readOnly ? '' : 'Clique para editar'}
              >
                {media.metadataJSON?.title || media.filename}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'center', fontSize: '0.65rem', lineHeight: 1.2 }}
              >
                {formatFileSize(media.file_size || 0)}
              </Typography>
            </>
          )}
        </Box>
        <CardActions
          sx={{
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 0,
            px: 0.25,
            py: 0.25,
            pt: 0,
            minHeight: 36,
          }}
        >
          {!readOnly && (
            <>
              <IconButton size="small" onClick={() => onPreview(media)} title="Visualizar" sx={{ p: 0.5 }}>
                <VisibilityIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => !editingTitle && setEditingTitle(true)}
                title="Editar título"
                sx={{ p: 0.5 }}
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(media)}
                title="Remover"
                sx={{ p: 0.5 }}
              >
                <DeleteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </>
          )}
          {readOnly && (
            <IconButton size="small" onClick={() => onPreview(media)} title="Visualizar" sx={{ p: 0.5 }}>
              <VisibilityIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </CardActions>
      </Card>
    </Grid>
  );
}

const MediaGallery = ({
  metadataKey,
  metadataValue,
  accept = 'image/*',
  maxFiles,
  onMediaChange,
  readOnly = false,
  hideEmptyReadOnly = false,
  hideEmptyPlaceholder = false,
  refreshKey,
  enablePaste: enablePasteProp,
  compact = false,
  selectExistingScope = null,
  metadataLinkUrl = '',
}) => {
  const { translate } = useTranslation();
  const enablePaste = enablePasteProp !== undefined ? enablePasteProp : !readOnly;
  const galleryKey = getGalleryKey(metadataKey, metadataValue);
  const dropZoneRef = useRef(null);
  const handleFileSelectRef = useRef(null);
  const onMediaChangeRef = useRef(onMediaChange);

  onMediaChangeRef.current = onMediaChange;

  const resolveMetadataUrl = useCallback(() => {
    if (metadataLinkUrl) return metadataLinkUrl;
    if (metadataKey === 'product_image' && metadataValue) {
      return `/products/${metadataValue}/edit`;
    }
    return '';
  }, [metadataKey, metadataLinkUrl, metadataValue]);
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const deferredUploadProgress = useDeferredValue(uploadProgress);
  const [dragOver, setDragOver] = useState(false);
  const [previewDialog, setPreviewDialog] = useState({ open: false, media: null });
  const [selectDialog, setSelectDialog] = useState({ open: false, selectedMedias: [] });
  const [availableMedias, setAvailableMedias] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadMedias = async () => {
    if (!metadataValue) {
      setMedias([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getMedias();
      const allMedias = response.data.medias || [];
      
      const filteredMedias = allMedias
        .map((media) => {
          if (!media.metadata || media.metadata.length === 0) {
            return null;
          }
          
          const matchingMeta = media.metadata.find((meta) => {
            if (meta.key !== metadataKey) return false;
            const metaValue = meta.value;
            const isObject = typeof metaValue === 'object' && metaValue !== null;
            if (isObject) {
              return metaValue.id === metadataValue;
            }
            return metaValue === metadataValue;
          });
          
          if (!matchingMeta) {
            return null;
          }
          
          const metaValue = matchingMeta.value;
          const isObject = typeof metaValue === 'object' && metaValue !== null;
          
          if (!isObject) {
            return {
              ...media,
              metadataJSON: { id: metadataValue, url: '', title: media.filename, order: 0 },
              order: 0,
            };
          }
          
          return {
            ...media,
            metadataJSON: metaValue,
            order: metaValue?.order || 0,
          };
        })
        .filter((media) => media !== null)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      startTransition(() => {
        setMedias(filteredMedias);
        onMediaChangeRef.current?.(filteredMedias);
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMedias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadataKey, metadataValue, refreshKey]);

  const handleFileSelect = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length === 0) {
      return;
    }

    if (maxFiles && medias.length + files.length > maxFiles) {
      setSnackbar({
        open: true,
        message: `Máximo de ${maxFiles} arquivos permitidos`,
        severity: 'error',
      });
      return;
    }

    if (event.target && 'value' in event.target) {
      event.target.value = '';
    }

    const progressMap = {};
    files.forEach((file) => {
      progressMap[file.name] = 0;
    });
    
    startTransition(() => {
      setUploadProgress(progressMap);
    });
    
    const startUpload = () => {
      setUploading(true);
      
      const uploadPromises = files.map(async (file, index) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('metadata_key', metadataKey);
        
        const metadataJSON = {
          id: metadataValue,
          url: resolveMetadataUrl(),
          title: file.name,
          order: medias.length + index,
        };
        formData.append('metadata_json', JSON.stringify(metadataJSON));
        
        startTransition(() => {
          setUploadProgress((prev) => ({ ...prev, [file.name]: 20 }));
        });
        
        try {
          const result = await uploadMedia(formData);
          startTransition(() => {
            setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
          });
          return result;
        } catch (err) {
          startTransition(() => {
            setUploadProgress((prev) => ({ ...prev, [file.name]: -1 }));
          });
          throw err;
        }
      });

      Promise.all(uploadPromises)
        .then(() => {
          startTransition(() => {
            setSnackbar({
              open: true,
              message: translate('media.uploaded'),
              severity: 'success',
            });
            setUploadProgress({});
            setUploading(false);
          });
          setTimeout(() => {
            startTransition(() => {
              loadMedias();
            });
          }, 500);
        })
        .catch((error) => {
          setSnackbar({
            open: true,
            message: error.message,
            severity: 'error',
          });
          startTransition(() => {
            setUploadProgress({});
            setUploading(false);
          });
        });
    };
    
    if (window.requestIdleCallback) {
      window.requestIdleCallback(startUpload, { timeout: 0 });
    } else {
      setTimeout(startUpload, 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadMedias intentionally omitted to avoid reload loops
  }, [maxFiles, medias.length, metadataKey, metadataValue, resolveMetadataUrl, translate]);

  handleFileSelectRef.current = handleFileSelect;

  const applyFilesList = useCallback((rawFiles) => {
    const matched = filesMatchAccept(rawFiles, accept);
    if (!matched.length) return;
    const dt = new DataTransfer();
    matched.forEach((file) => dt.items.add(file));
    handleFileSelectRef.current?.({
      target: { files: dt.files, value: '' },
    });
  }, [accept]);

  const handleGalleryPointerDown = useCallback(() => {
    lastActiveGalleryKey = galleryKey;
  }, [galleryKey]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!readOnly) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    if (readOnly) {
      return;
    }

    lastActiveGalleryKey = galleryKey;
    const files = extractFilesFromDataTransfer(e.dataTransfer);
    if (files.length > 0) {
      applyFilesList(files);
    }
  };

  const handlePasteFromClipboard = useCallback(async () => {
    lastActiveGalleryKey = galleryKey;
    dropZoneRef.current?.focus();
    const { files, unsupported } = await extractFilesFromClipboardApi();
    const matched = filesMatchAccept(files, accept);
    if (matched.length > 0) {
      applyFilesList(matched);
      return;
    }
    if (unsupported) {
      setSnackbar({
        open: true,
        message: translate('media.paste_unavailable'),
        severity: 'info',
      });
      return;
    }
    setSnackbar({
      open: true,
      message: translate('media.paste_unavailable'),
      severity: 'info',
    });
  }, [galleryKey, accept, applyFilesList, translate]);

  useEffect(() => {
    if (!enablePaste || readOnly || !metadataValue) {
      return undefined;
    }

    if (!pasteGalleryRegistry.includes(galleryKey)) {
      pasteGalleryRegistry.push(galleryKey);
    }

    const handleDocumentPaste = (e) => {
      const fallbackKey = pasteGalleryRegistry[0] || null;
      const targetKey = lastActiveGalleryKey || fallbackKey;
      if (targetKey !== galleryKey) {
        return;
      }
      const raw = extractFilesFromDataTransfer(e.clipboardData);
      const matched = filesMatchAccept(raw, accept);
      if (!matched.length) {
        return;
      }
      e.preventDefault();
      applyFilesList(matched);
    };

    document.addEventListener('paste', handleDocumentPaste);
    return () => {
      document.removeEventListener('paste', handleDocumentPaste);
      const idx = pasteGalleryRegistry.indexOf(galleryKey);
      if (idx >= 0) {
        pasteGalleryRegistry.splice(idx, 1);
      }
      if (lastActiveGalleryKey === galleryKey) {
        lastActiveGalleryKey = null;
      }
    };
  }, [enablePaste, readOnly, metadataValue, galleryKey, accept, applyFilesList]);

  const handleDragEnd = async (event) => {
    if (readOnly) {
      return;
    }
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = medias.findIndex((media) => media.id === active.id);
    const newIndex = medias.findIndex((media) => media.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const newMedias = arrayMove(medias, oldIndex, newIndex);
    setMedias(newMedias);

    try {
      const mediaOrders = newMedias.map((media, index) => ({
        media_id: media.id,
        order: index,
      }));

      await updateMediaOrder(metadataKey, metadataValue, mediaOrders);
      
      setSnackbar({
        open: true,
        message: translate('media.order_updated'),
        severity: 'success',
      });
      
      onMediaChangeRef.current?.(newMedias);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao atualizar ordem',
        severity: 'error',
      });
      await loadMedias();
    }
  };

  const handlePreview = (media) => {
    setPreviewDialog({ open: true, media });
  };

  useEffect(() => {
    if (!previewDialog.open || !previewDialog.media) return;
    if (medias.findIndex((m) => m.id === previewDialog.media.id) === -1) {
      setPreviewDialog({ open: false, media: null });
    }
  }, [previewDialog.open, previewDialog.media, medias]);

  const handleEditTitle = async (mediaId, newTitle) => {
    try {
      const media = medias.find((m) => m.id === mediaId);
      if (!media || !media.metadataJSON) {
        return;
      }

      // Se for product_image e não tiver URL, adiciona automaticamente
      let url = media.metadataJSON.url || resolveMetadataUrl();

      await updateMediaMetadata(
        mediaId,
        metadataKey,
        media.metadataJSON.id,
        newTitle,
        url
      );

      setSnackbar({
        open: true,
        message: translate('media.title_updated'),
        severity: 'success',
      });

      await loadMedias();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao atualizar título',
        severity: 'error',
      });
    }
  };

  const handleDeleteClick = async (media) => {
    try {
      const response = await getMediaLinks(media.id);
      const links = response.data.links || [];
      
      if (links.length === 1) {
        await deleteMedia(media.id);
        setSnackbar({
          open: true,
          message: translate('media.deleted'),
          severity: 'success',
        });
      } else {
        const matchingMeta = media.metadata?.find((m) => {
          if (m.key !== metadataKey) return false;
          const metaValue = m.value;
          const isObject = typeof metaValue === 'object' && metaValue !== null;
          if (isObject) {
            return metaValue.id === metadataValue;
          }
          return metaValue === metadataValue;
        });
        if (!matchingMeta) {
          throw new Error(translate('media.unlink_failed') || 'Could not resolve link to remove');
        }
        const metaValue = matchingMeta.value;
        // Backend UnlinkMediaUsecase matches JSON metadata by unmarshaled .id === metadata_value (string).
        // Sending JSON.stringify(metaValue) breaks that match and the DB fallback needs byte-identical metadata_value.
        let valueToSend = metaValue;
        if (typeof metaValue === 'object' && metaValue !== null && metaValue.id != null && String(metaValue.id).trim() !== '') {
          valueToSend = String(metaValue.id);
        } else if (typeof metaValue === 'object' && metaValue !== null) {
          valueToSend = JSON.stringify(metaValue);
        }
        await unlinkMedia(media.id, metadataKey, valueToSend);
        setSnackbar({
          open: true,
          message: translate('media.unlinked'),
          severity: 'success',
        });
      }
      
      await loadMedias();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleSelectExistingClick = async () => {
    try {
      setLoadingAvailable(true);
      const response = await getMedias();
      const allMedias = response.data.medias || [];
      
      const acceptImagesOnly = (accept || '').includes('image');

      let pool = allMedias;
      if (selectExistingScope?.metadataKey && selectExistingScope?.metadataValue) {
        pool = allMedias.filter((media) =>
          mediaMatchesMetadataScope(
            media,
            selectExistingScope.metadataKey,
            selectExistingScope.metadataValue,
          ),
        );
      }

      const availableMedias = pool.filter((media) => {
        if (acceptImagesOnly && !isImageMimeOrExtension(media.mime_type, media.filename)) {
          return false;
        }

        if (!media.metadata || media.metadata.length === 0) {
          return !selectExistingScope;
        }

        const alreadyLinked = media.metadata.some((meta) => {
          if (meta.key !== metadataKey) return false;
          const metaValue = meta.value;
          const isObject = typeof metaValue === 'object' && metaValue !== null;
          if (isObject) {
            return metaValue.id === metadataValue;
          }
          return metaValue === metadataValue;
        });
        
        return !alreadyLinked;
      });

      setAvailableMedias(availableMedias);
      setSelectDialog({ open: true, selectedMedias: [] });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoadingAvailable(false);
    }
  };

  const handleToggleMediaSelection = (mediaId) => {
    setSelectDialog((prev) => {
      const isSelected = prev.selectedMedias.includes(mediaId);
      return {
        ...prev,
        selectedMedias: isSelected
          ? prev.selectedMedias.filter((id) => id !== mediaId)
          : [...prev.selectedMedias, mediaId],
      };
    });
  };

  const handleLinkSelectedMedias = async () => {
    const { selectedMedias } = selectDialog;
    if (selectedMedias.length === 0) {
      setSnackbar({
        open: true,
        message: translate('media.select_at_least_one'),
        severity: 'error',
      });
      return;
    }

    try {
      setUploading(true);
      const linkPromises = selectedMedias.map(async (mediaId, index) => {
        const metadataJSON = {
          id: metadataValue,
          url: resolveMetadataUrl(),
          title: '',
          order: medias.length + index,
        };

        return linkMedia(mediaId, {
          [metadataKey]: JSON.stringify(metadataJSON),
        });
      });

      await Promise.all(linkPromises);
      setSnackbar({
        open: true,
        message: translate('media.linked_success'),
        severity: 'success',
      });
      setSelectDialog({ open: false, selectedMedias: [] });
      await loadMedias();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSelectCancel = () => {
    setSelectDialog({ open: false, selectedMedias: [] });
  };

  if (!metadataValue) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {translate('media.save_first')}
        </Typography>
      </Box>
    );
  }

  if (readOnly && hideEmptyReadOnly && !loading && medias.length === 0) {
    return null;
  }

  const uploadDisabled = Boolean(maxFiles && medias.length >= maxFiles);
  const acceptHintKey = getAcceptHintKey(accept);
  const showFolderPathHint = isOrderMediaMetadataKey(metadataKey);

  return (
    <Box onPointerDown={handleGalleryPointerDown}>
      {!readOnly && (
        <Box
          ref={dropZoneRef}
          tabIndex={0}
          role="region"
          aria-label={translate('media.upload_images')}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            mb: compact ? 1.5 : 2,
            p: compact ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 3 },
            border: '1px dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            borderRadius: 2,
            bgcolor: dragOver ? 'action.hover' : 'background.paper',
            textAlign: 'center',
            transition: 'border-color 0.15s ease, background-color 0.15s ease',
            outline: 'none',
            '&:focus-visible': {
              borderColor: 'primary.main',
              boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 1,
            }}
          >
            <input
              accept={accept}
              style={{ display: 'none' }}
              id={`media-gallery-upload-${metadataKey}-${metadataValue}`}
              type="file"
              multiple
              onChange={handleFileSelect}
              disabled={uploadDisabled}
              key={`upload-${metadataKey}-${metadataValue}`}
            />
            <label htmlFor={`media-gallery-upload-${metadataKey}-${metadataValue}`}>
              <Button
                variant="outlined"
                component="span"
                size="small"
                disabled={uploadDisabled || uploading}
              >
                {uploading ? translate('common.uploading') : translate('media.upload_images')}
              </Button>
            </label>
            <Button
              variant="text"
              size="small"
              onClick={handleSelectExistingClick}
              disabled={uploading}
            >
              {translate('media.select_existing')}
            </Button>
            {enablePaste && (
              <Button
                variant="text"
                size="small"
                startIcon={<ContentPasteIcon sx={{ fontSize: 18 }} />}
                onClick={handlePasteFromClipboard}
                disabled={uploadDisabled || uploading}
              >
                {translate('media.paste_from_clipboard')}
              </Button>
            )}
          </Box>
          {uploading && Object.keys(deferredUploadProgress).length > 0 && (
            <Box sx={{ mt: 1, mb: 1, maxWidth: 480, mx: 'auto', textAlign: 'left' }}>
              {Object.entries(deferredUploadProgress).map(([fileName, progress]) => (
                <Box key={fileName} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" noWrap sx={{ flex: 1, mr: 1, fontSize: '0.7rem' }}>
                      {fileName}
                    </Typography>
                    {progress === 100 && <Chip label="Concluído" size="small" color="success" sx={{ height: 20 }} />}
                    {progress === -1 && <Chip label="Erro" size="small" color="error" sx={{ height: 20 }} />}
                    {progress > 0 && progress < 100 && (
                      <Chip label={`${progress}%`} size="small" sx={{ height: 20 }} />
                    )}
                  </Box>
                  {progress > 0 && progress < 100 && (
                    <LinearProgress variant="determinate" value={progress} />
                  )}
                  {progress === 100 && (
                    <LinearProgress variant="determinate" value={100} color="success" />
                  )}
                  {progress === -1 && (
                    <LinearProgress variant="determinate" value={100} color="error" />
                  )}
                </Box>
              ))}
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            {translate(acceptHintKey)}
            {enablePaste ? ` ${translate('media.paste_hint')}` : ''}
            {maxFiles ? ` (${medias.length}/${maxFiles})` : ''}
          </Typography>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : medias.length === 0 ? (
        hideEmptyPlaceholder || !readOnly ? null : (
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={3} md={2} lg={2}>
            <Card
              sx={{
                position: 'relative',
                height: '100%',
                overflow: 'hidden',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
              }}
            >
              <Box sx={{ ...mediaThumbAreaSx(compact), cursor: 'default' }}>
                <Box
                  component="img"
                  src={PRODUCT_DEFAULT_IMAGE}
                  alt={translate('media.no_images')}
                  sx={mediaThumbImgSx}
                />
              </Box>
              <Box sx={{ px: 0.75, py: 0.75 }}>
                <Typography variant="caption" display="block" textAlign="center" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {translate('media.no_images')}
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>
        )
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={medias.map((m) => m.id)}
            strategy={rectSortingStrategy}
          >
            <Grid container spacing={compact ? 1 : 1.5}>
              {medias.map((media) => (
                <SortableMediaItem
                  key={media.id}
                  media={media}
                  onPreview={handlePreview}
                  onDelete={handleDeleteClick}
                  onEditTitle={handleEditTitle}
                  readOnly={readOnly}
                  compact={compact}
                  showFolderPathHint={showFolderPathHint}
                />
              ))}
            </Grid>
          </SortableContext>
        </DndContext>
      )}

      <MediaLightbox
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, media: null })}
        items={medias}
        activeMedia={previewDialog.media}
        onNavigate={(media) => setPreviewDialog({ open: true, media })}
        apiBaseUrl={API_URL}
        desktopOpenContext={resolveDesktopOpenContext(metadataKey, metadataValue)}
      />

      <Dialog
        open={selectDialog.open}
        onClose={handleSelectCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{translate('media.select_existing_images')}</DialogTitle>
        <DialogContent>
          {loadingAvailable ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : availableMedias.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {translate('media.no_available_images')}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={1.5} sx={{ mt: 1 }}>
              {availableMedias.map((media) => (
                <Grid item xs={6} sm={3} md={2} key={media.id}>
                  <Card
                    role="button"
                    tabIndex={0}
                    aria-pressed={selectDialog.selectedMedias.includes(media.id)}
                    aria-label={media.filename}
                    sx={{
                      cursor: 'pointer',
                      overflow: 'hidden',
                      borderRadius: 1.5,
                      boxShadow: 'none',
                      border: selectDialog.selectedMedias.includes(media.id)
                        ? '2px solid'
                        : '1px solid',
                      borderColor: selectDialog.selectedMedias.includes(media.id)
                        ? 'primary.main'
                        : 'divider',
                    }}
                    onClick={() => handleToggleMediaSelection(media.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleToggleMediaSelection(media.id);
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <Box sx={{ ...mediaThumbAreaSx(false), maxHeight: { xs: 88, sm: 96 }, cursor: 'inherit' }}>
                        {isImageMimeOrExtension(media.mime_type, media.filename) ? (
                          <Box
                            component="img"
                            src={`${API_URL}${media.file_path}`}
                            alt={media.filename}
                            sx={mediaThumbImgSx}
                            loading="lazy"
                          />
                        ) : isVideoMimeOrExtension(media.mime_type, media.filename) ? (
                          <Box
                            component="video"
                            src={`${API_URL}${media.file_path}`}
                            muted
                            playsInline
                            preload="metadata"
                            sx={{ ...mediaThumbImgSx, pointerEvents: 'none' }}
                          />
                        ) : (
                          getFileIcon(media.mime_type, media.filename, 32)
                        )}
                      </Box>
                      <Checkbox
                        checked={selectDialog.selectedMedias.includes(media.id)}
                        size="small"
                        sx={(theme) => ({
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: alpha(theme.palette.background.paper, 0.95),
                          borderRadius: '4px',
                          p: 0,
                        })}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleMediaSelection(media.id);
                        }}
                      />
                    </Box>
                    <CardActions sx={{ px: 0.75, py: 0.5, justifyContent: 'center', flexDirection: 'column' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          width: '100%',
                          textAlign: 'center',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.25,
                          wordBreak: 'break-word',
                        }}
                      >
                        {media.filename}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
                        {formatFileSize(media.file_size || 0)}
                      </Typography>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSelectCancel}>
            {translate('common.cancel')}
          </Button>
          <Button
            onClick={handleLinkSelectedMedias}
            variant="contained"
            disabled={selectDialog.selectedMedias.length === 0 || uploading}
          >
            {uploading ? translate('common.loading') : translate('common.add')} ({selectDialog.selectedMedias.length})
          </Button>
        </DialogActions>
      </Dialog>

      <SavingOverlay open={uploading} messageKey="common.uploading" />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MediaGallery;
